import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Site {
  id: string; domain: string; baseUrl: string; verified: boolean; businessType: string
  createdAt: string
  tenant: { id: string; name: string; plan: string }
}

export function AdminSitesPage() {
  const t = useT()
  const [q, setQ] = useState('')
  const [verifiedOnly, setVerifiedOnly] = useState(false)
  const sites = useQuery({
    queryKey: ['admin-sites', q, verifiedOnly],
    queryFn: () => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (verifiedOnly) params.set('verified', 'true')
      return apiClient.get<{ data: Site[] }>(`/admin/sites?${params}`)
    },
  })

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">{t('admin.sites.title')}</h1>
      <div className="flex gap-2 items-center mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('admin.sites.search')}
          className="px-2 py-1 text-sm border rounded bg-transparent w-64"
          style={{ borderColor: 'var(--color-border)' }}
        />
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
          {t('admin.sites.verified_only')}
        </label>
      </div>
      {sites.isLoading && <p>{t('common.loading')}</p>}
      {sites.data && (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-2 pr-4">{t('admin.sites.column.domain')}</th>
              <th className="py-2 pr-4">{t('admin.sites.column.tenant')}</th>
              <th className="py-2 pr-4">{t('admin.sites.column.business')}</th>
              <th className="py-2 pr-4">{t('admin.sites.column.verified')}</th>
              <th className="py-2 pr-4">{t('admin.sites.column.created')}</th>
            </tr>
          </thead>
          <tbody>
            {sites.data.data.map((s) => (
              <tr key={s.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-2 pr-4">
                  <a href={s.baseUrl} target="_blank" rel="noreferrer" className="hover:underline">{s.domain}</a>
                </td>
                <td className="py-2 pr-4">
                  <Link to={`/admin/tenants/${s.tenant.id}`} className="hover:underline">{s.tenant.name}</Link>
                  <span className="text-xs opacity-60 ml-1">({s.tenant.plan})</span>
                </td>
                <td className="py-2 pr-4">{s.businessType}</td>
                <td className="py-2 pr-4">{s.verified ? '✓' : '—'}</td>
                <td className="py-2 pr-4">{new Date(s.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
